<template>
    <div class="autocomplete control">
        <input
            class="input"
            :class="inputClass"
            ref="inputRef"
            type="text"
            v-model="model"
            @input="onChange"
            @keydown.down="onArrowDown"
            @keydown.up="onArrowUp"
            @keydown.enter="onEnter"
            @keydown.tab="onTab"
            @keydown.esc="onEscape"
        />
        <slot name="after-input" />
        <ul v-show="isOpen" class="autocomplete-results">
            <li
                v-for="(result, i) in results"
                :key="i"
                class="autocomplete-result"
                :class="{ 'is-active': i === arrowCounter }"
                @click="setResult(result)"
                @mouseover="arrowCounter = i"
            >
                <slot :result-item="result" name="result-item" />
            </li>
        </ul>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted, onUnmounted } from "vue";
    import { KEY_ENTER, KEY_TAB, KEY_TAB_SHIFT } from "../Common/consts";

    const model = defineModel<string>({ required: true });

    const props = withDefaults(
        defineProps<{
            items: string[];
            resultsFilter: (item: string, search: string) => boolean;
            autoFocus?: boolean;
            inputClass?: string;
        }>(),
        {
            autoFocus: false,
            inputClass: "",
        },
    );

    const emit = defineEmits<{
        confirm: [data: [string, boolean]];
        cancel: [];
    }>();

    const results = ref<string[]>([]);
    const isOpen = ref(false);
    const arrowCounter = ref(-1);
    const changed = ref(false);
    const inputRef = ref<HTMLInputElement | null>(null);

    onMounted(() => {
        if (props.autoFocus) {
            // Huge hack, I don't know how to bypass it now:
            // The problem is when I press tab when switching between inputs
            // It fires first ENABLE_EDIT_MODE and then native tab support in browser is triggered.
            // It means that input gets focus, then some random element on page.
            // By having 10ms of timeout it gets back to input. Silly but works :shrug:
            setTimeout(() => {
                selectText();
            }, 10);
        }
        document.addEventListener("click", handleClickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener("click", handleClickOutside);
    });

    function handleClickOutside(evt: MouseEvent) {
        const target = evt.target as Node;
        if (inputRef.value && !inputRef.value.contains(target)) {
            isOpen.value = false;
            arrowCounter.value = -1;
        }
    }

    function onChange() {
        changed.value = true;
        filterResults();
    }

    function filterResults() {
        if (model.value.length === 0) {
            results.value = [];
            isOpen.value = false;
            return;
        }

        results.value = props.items
            .filter(item => {
                return props.resultsFilter(item, model.value);
            })
            .slice(0, 8);
        isOpen.value = results.value.length > 0;

        if (results.value.length < arrowCounter.value) {
            arrowCounter.value = results.value.length - 1;
        }
    }

    function setResult(result: string) {
        model.value = result;
        isOpen.value = false;
    }

    function onArrowDown() {
        if (arrowCounter.value < results.value.length - 1) {
            arrowCounter.value = arrowCounter.value + 1;
        }
    }

    function onArrowUp() {
        if (arrowCounter.value > 0) {
            arrowCounter.value = arrowCounter.value - 1;
        }
    }

    function onEnter() {
        if (arrowCounter.value === -1) {
            emit("confirm", [KEY_ENTER, changed.value]);
            isOpen.value = false;
            return;
        }
        setResult(results.value[arrowCounter.value]);
        arrowCounter.value = -1;
    }

    function onTab(e: KeyboardEvent) {
        if (arrowCounter.value === -1) {
            emit("confirm", [e.shiftKey ? KEY_TAB_SHIFT : KEY_TAB, changed.value]);
            isOpen.value = false;
        }
    }

    function onEscape() {
        arrowCounter.value = -1;
        isOpen.value = false;
        emit("cancel");
    }

    function selectText() {
        if (inputRef.value) {
            inputRef.value.focus();
            inputRef.value.select();
        }
    }

    defineExpose({
        selectText,
    });
</script>

<style scoped>
    /* Having .control class is mandatory in order to make css vars available. */

    .autocomplete {
        position: relative;
    }

    .autocomplete-results {
        position: absolute;
        z-index: 100;
        padding: 0;
        margin: 5px 0 0;
        border: 1px solid hsl(var(--bulma-input-h), var(--bulma-input-s), var(--bulma-input-border-l));
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        border-radius: var(--bulma-input-radius);
        min-height: 50px;
        max-height: 200px;
        overflow: auto;
        background-color: hsl(var(--bulma-input-h), var(--bulma-input-s), var(--bulma-input-background-l));
        min-width: 300px;
    }

    .autocomplete-result {
        list-style: none;
        text-align: left;
        padding: 4px 2px;
        color: hsl(var(--bulma-input-h), var(--bulma-input-s), var(--bulma-input-color-l));
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s ease;
    }

    .autocomplete-result:hover {
        background-color: hsl(
            var(--bulma-input-h),
            var(--bulma-input-s),
            calc(var(--bulma-input-background-l) + var(--bulma-hover-background-l-delta))
        );
    }

    .autocomplete-result.is-active {
        background-color: hsl(
            var(--bulma-input-h),
            var(--bulma-input-s),
            calc(var(--bulma-input-background-l) + var(--bulma-hover-background-l-delta))
        );
    }
</style>
